import { useState, useRef, useEffect, useContext, memo, useMemo, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useAuth0 } from "@auth0/auth0-react";
import { useReactFlow, getOutgoers } from "reactflow";
import SoftBox from "components/SoftBox";
import Tooltip from "@mui/material/Tooltip";
import IconButton from "@mui/material/IconButton";
import ToggleDrawer from "layouts/Flow Designer/components/ToogleDrawer";
import { CustomNodeContext } from "layouts/Flow Designer/context";
import { ConditionalWrapper } from "helpers/common";
import DeleteNodeMenuItem from "../DeleteNodeDialog";
import { COMMON_COMPONENT_TYPES, REQUIRED_CREDENTIAL_COMPONENTS } from "constants/flowDesigner";
import { hideComponentPropsPanel } from "redux/actions/flowDesigner/componentPropsPanelActionCreator";
import CustomNodeHandle from "layouts/Flow Designer/components/CustomNodes/components/CustomNodeHandle";
import useGetBranchNodes from "hooks/useGetBranchNodes";
import useComponentIdGenerator from "hooks/useComponentIdGenerator";
import useFlowStepConnection from "hooks/useFlowStepConnection";
import { addLabelNodes } from "layouts/Flow Designer/helpers/common";
import { executeFlow } from "redux/actions/flowDesigner/flowExecutionActionCreator";
import { TabContextApi } from "layouts/Flow Designer/context";
import commonComponentsMockData from "layouts/Flow Designer/data/commonComponentsMockData";
import { useCopyPaste } from "hooks/useCopyPaste";
import { setIsNotesDialogOpen, setNodeToAddNotes } from "redux/actions/flowDesigner/flowsActionCreator";
import useDeleteNode from "hooks/useDeleteNode";
import ButtonNotes from "../ButtonNotes";
import ButtonRename from "./ButtonRename";
import ButtonCopyNode from "./ButtonCopyNode";
import ButtonCloneNode from "./ButtonCloneNode";
import ButtonAddBranch from "./ButtonAddBranch";
import ButtonSwitchState from "./ButtonSwitchState";
import useAllowEditWorkflow from "hooks/useAllowEditWorkflow";
import NodeLabel from "../NodeLabel";
import connectorpng from "assets/images/connector.png";
import { useDetermineShareFlowUserAccessType } from "hooks/useDetermineShareFlowUserAccessType";
import { ThemedPlayArrowIcon } from "assets/CXFIcons/Designer/ContextActions";
import ButtonCutNode from "./ButtonCutNode";
import { useCutPaste } from "hooks/useCutPaste";
import { useFlexLayout } from "context/AppProvider/FlexLayoutContext";
import { EDGE_TYPES, FLEX_LAYOUT_COMPONENT, NODE_TYPES } from "constants/flowDesigner";
import { showComponentPropsPanel } from "redux/actions/flowDesigner/componentPropsPanelActionCreator";
import labelComponentMockData from "layouts/Flow Designer/data/labelComponentMockData";
import { setSelectedLogNodeId } from "redux/actions/flowDesigner/flowExecutionActionCreator";
import { FlowStateContextApi } from "layouts/Flow Designer/context";
import { ActionsWrapper } from "./ActionsWrapper";

function CustomNode({ data, id, selected, isCommon, isLabel, generateTooltip = Function() }) {
   const { selectedLogNodeId, flowReports } = useSelector((store) => store.designer.flowExecution);
   const toOpenNodeTab = useSelector((store) => store.designer.componentPropsPanel.toOpenNodeTab);
   const { t } = useTranslation("flowDesigner");
   const { isVerticalCanvasDirection } = useSelector((state) => state.designer?.settings);
   const { currentSavedFlow, flows } = useSelector((state) => state.designer?.flows);
   const isFlowActive = currentSavedFlow?.shared_status === "active";
   const [flowElement, setFlowElement] = useState(null);
   const [showTooltip, setShowTooltip] = useState(false);
   const [node, setNode] = useState({});
   const reactFlowInstance = useReactFlow();
   const { getNode, getNodes, getEdges, setNodes, setEdges, screenToFlowPosition } = reactFlowInstance;
   const { getChildNodeIdsBeforeMergeNode, getMergeNodeId } = useGetBranchNodes();
   const { generateNodeId } = useComponentIdGenerator();
   const { addHiddenBranchNodes } = useFlowStepConnection();
   const { autoSaveFlow } = useContext(TabContextApi);
   const { copy, paste } = useCopyPaste();
   const { cut } = useCutPaste();
   const dispatch = useDispatch();
   const { user } = useAuth0();
   const { deleteNode: deleteNodeFromCanvas } = useDeleteNode();
   const { isFlowDraft, allowEditWorkflow } = useAllowEditWorkflow();
   const drawerRef = useRef();
   let whatNode = data;
   whatNode.id = data?.id;
   const [hasMissingRequiredField, setHasMissingRequiredFields] = useState(false);
   const { isReadOnlyAccess } = useDetermineShareFlowUserAccessType(currentSavedFlow?.id);
   const { addLayoutTab } = useFlexLayout();
   const tabId = `componentProps-${id}`;
   const activeTab = useSelector((store) => store.designer.tabs.activeFlexLayoutTab);
   const executionPanelActive = activeTab === "executionReport";
   const { takeSnapshot } = useContext(FlowStateContextApi);
   
   const handleOpenComponentProps = (tab) => {
      const selectedNodeSchema = isCommon ? commonComponentsMockData[data?.type] : isLabel ? labelComponentMockData : data?.componentFiles?.schema;

      const isSelectionNode = selectedNodeSchema?.key === "selectionComponent";
      const node = reactFlowInstance?.getNode(id);
      const selectionNodeFlowElement = node?.data ? { ...node.data, elementId: node.id } : {};
      const selectedNode = {
         ...selectedNodeSchema,
         elementId: node?.id,
         data: node?.data
      };

      if (selectedNode && whatNode) {
         if (whatNode?.componentFiles?.schema) {
            selectedNode.connectorId = whatNode.componentId;
            selectedNode.version = whatNode.version || selectedNode.version || "1.0";
         }
      }

      const payload = {
         tabId,
         updateFlowElementCallback: updateFlowElement,
         selectedNode,
         title: data?.customLabel || data?.label,
         flowElement: flowElement ? (isSelectionNode ? selectionNodeFlowElement : flowElement) : {}
      };

      const isWideComponent = data?.type === COMMON_COMPONENT_TYPES.VARIABLES || data?.type === COMMON_COMPONENT_TYPES.INVOKE;

      addLayoutTab(tab, tabId, data?.customLabel || data?.label, { nodeId: id, wide: isWideComponent });
      dispatch(showComponentPropsPanel(payload));
   };
   const { components } = useSelector((store) => store.designer.cxfComponents);

     const updateFlowElement = (el) => {
        if (!isCommon && !isLabel) {
           setFlowElement(el);
           setNodes((nodes) => nodes.map((current) => current.id === id
              ? { ...current, data: { ...current.data, flowElement: el } }
              : current));
           return;
        }
        const updatedNode = getNode(node?.id);
        setFlowElement(el);

        if (updatedNode?.data) {
           setNode({ ...updatedNode.data, flowElement: el });
        } else {
           setNode({ ...node, flowElement: el });
        }
        data.flowElement = el;
     };

   const isBooleanNode = () => {
      return data?.type === COMMON_COMPONENT_TYPES.CONDITIONAL || data?.type === COMMON_COMPONENT_TYPES.SELECTION;
   };

   const checkHierarchy = () => {
      const curNode = getNode(id);

      if (curNode) {
         const nodes = getNodes();
         const edges = getEdges();
         let outgoers = getOutgoers(curNode, nodes, edges);

         return !!outgoers.length;
      }
      return false;
   };

   const renameNode = useCallback(
      (e) => {
         e.stopPropagation();
         handleOpenComponentProps(FLEX_LAYOUT_COMPONENT.componentProps);
      },
      [handleOpenComponentProps]
   );

   const copyNode = useCallback(
      (e) => {
         e.stopPropagation();
         copy(id);
      },
      [id]
   );

   const cutNode = useCallback(
      (e) => {
         e.stopPropagation();
         cut(id);
         deleteNodeFromCanvas(id, data);
      },
      [id]
   );

   const executeNode = (e) => {
      e.stopPropagation();
      addLayoutTab(FLEX_LAYOUT_COMPONENT.executionReport, `executionReport`, `Logs`);
      dispatch(
         executeFlow(user?.stripeId, {
            flow_id: currentSavedFlow.id,
            singleStepId: id,
            targetUserId: user?.cxfUserId
         })
      );
   };
   const disableSubFlowNode = (parentNode, isDisabled, nodes) => {
      const updatedNodes = nodes.map((node) => {
         if (node.type === NODE_TYPES.SUBFLOW && node.data?.parentNodeId === parentNode.id) {
            return {
               ...node,
               data: {
                  ...node.data,
                  isDisabled: isDisabled
               }
            };
         }
         return node;
      });

      return updatedNodes;
   };

   const toggleNodeState = (e) => {
      e.stopPropagation();
      const curNode = getNode(id);
      let nodes = getNodes();

      if (curNode) {
         const isDisabled = curNode.data.isDisabled;
         if (isBooleanNode()) {
            const childNodeIds = getChildNodeIdsBeforeMergeNode(curNode);

            childNodeIds.forEach((id) => {
               const childNode = getNode(id);
               if (childNode && childNode.data) {
                  childNode.data.isDisabled = !isDisabled;
               }
               if (childNode?.data?.type === COMMON_COMPONENT_TYPES.INVOKE) {
                  nodes = disableSubFlowNode(childNode, !isDisabled, nodes);
               }
            });
         }
         curNode.data.isDisabled = !isDisabled;
         if (curNode.data?.type === COMMON_COMPONENT_TYPES.INVOKE) {
            nodes = disableSubFlowNode(curNode, !isDisabled, nodes);
         }

         setNodes([...nodes]);
         autoSaveFlow(reactFlowInstance);
      }
   };

   const deleteNode = useCallback(() => {
      deleteNodeFromCanvas(id, data);
   }, [id, data, deleteNodeFromCanvas]);

   const handleAddBranchButtonClick = () => {
      if (isFlowActive || !allowEditWorkflow(isReadOnlyAccess)) return;

      const curNode = getNode(id);

      hideSelectedNodePanel();

      if (curNode) {
         const { x, y } = curNode.position;
         const outgoers = getOutgoers(curNode, getNodes(), getEdges());
         const branchName = getNewBranchName(outgoers);
         const position = screenToFlowPosition({
            x: x,
            y: y
         });
         const mergeNodeId = getMergeNodeId(curNode);
         const params = {
            position,
            branchName,
            getId: generateNodeId,
            sourceId: id,
            targetId: mergeNodeId
         };
         const labelStepElements = addLabelNodes(params);
         const hiddenBranchData = addHiddenBranchNodes(labelStepElements, position, mergeNodeId, mergeNodeId);
         const newNodes = hiddenBranchData.labelNodes;
         const newEdges = hiddenBranchData.labelEdges;

         setNodes([...getNodes(), ...newNodes]);
         setEdges([...getEdges(), ...newEdges]);

         autoSaveFlow(reactFlowInstance);
      }
   };

   const getNewBranchName = (branches) => {
      let count = 0;

      do {
         count++;
      } while (branches.some((branch) => branch.data.label === `Case ${count}`));

      return `Case ${count}`;
   };

   const hideSelectedNodePanel = () => {
      dispatch(hideComponentPropsPanel());
   };

   const setNodeAndFlowElementData = () => {
      const flowElementData = data.flowElement;
      data.id = id;
      data.hasChildren = checkHierarchy();

      if (flowElementData) {
         setFlowElement(flowElementData);

         if (isLabel && flowElementData.data?.labelName?.value) {
            // data.label = flowElementData.data?.labelName?.value;
         }
      } else {
         setFlowElement(isCommon ? {} : { id: id, componentId: data?.componentId });
      }

      setNode(data);
   };

   const handleOpenNoteDialog = (e) => {
      e.stopPropagation();
      dispatch(setIsNotesDialogOpen(true));
      dispatch(setNodeToAddNotes(data));
   };

   const renderConnectorVersionNumber = () => {
      let text = "";
      if (!data?.isConnectorExist) {
         return <SoftBox className="connector connector__version_number missing_connector">?</SoftBox>;
      } else if (data?.version) {
         text = data.version;
      } else {
         text = "1.0";
      }

      return <SoftBox className="connector connector__version_number">{text}</SoftBox>;
   };

   const handleCloneNode = (e) => {
      e.stopPropagation();
      const node = getNode(id);
      const edges = getEdges();
      const controlFlowEdges = edges.filter((edge) => edge.type !== EDGE_TYPES.FLOATING && edge.type !== EDGE_TYPES.STRAIGHT);
      const nodes = getNodes();

      takeSnapshot();

      let cloneNodeData = {
         node: node
      };

      let source = null;
      let target = null;

      if (data.type === COMMON_COMPONENT_TYPES.CONDITIONAL || data.type === COMMON_COMPONENT_TYPES.SELECTION) {
         cloneNodeData = {
            ...cloneNodeData,
            originalNodes: nodes,
            originalEdges: edges
         };
         const mergeNode = nodes.find((n) => n.id === data.mergeId);

         source = mergeNode.id;
         target = getOutgoers(mergeNode, nodes, controlFlowEdges)[0].id;
      } else {
         source = data.id;
         target = getOutgoers(node, nodes, controlFlowEdges)[0].id;
      }

      paste(source, target, cloneNodeData);
   };

   useEffect(() => {
      setNodeAndFlowElementData();
   }, [data]);

   useEffect(() => {
      const nodeComponentAction = commonComponentsMockData[data.type]?.actions[0]?.dataInsideAction;
      const nodeFlowElement = data.flowElement;
      const nodeFlowElementData = nodeFlowElement.data;

      if (data.configFiles) {
         setHasMissingRequiredFields(nodeFlowElementData?.credentialRequired && !nodeFlowElementData?.credential?.value);

         return;
      }

       if (!nodeComponentAction) return;

       let isNodeMissingRequiredField = REQUIRED_CREDENTIAL_COMPONENTS.has(data.type) && !nodeFlowElementData?.credential?.value;

       if (nodeFlowElement?.component === COMMON_COMPONENT_TYPES.VARIABLES) {
          const variableFields = Object.entries(nodeFlowElementData ?? {}).filter(([field]) => field.startsWith("var_") || field.startsWith("reference_var_"));
          isNodeMissingRequiredField = variableFields.some(([, variable]) => !variable?.value);
          setHasMissingRequiredFields(isNodeMissingRequiredField);
          return;
       }

      if (nodeFlowElement?.component === COMMON_COMPONENT_TYPES.REDIS) {
         const redisAction = commonComponentsMockData[data.type]?.actions.find((action) => action.actionName === nodeFlowElement.action)?.dataInsideAction;

         redisAction.forEach((action) => {
            if (!action.isRequired) return;

            if (!nodeFlowElementData || !nodeFlowElementData[action.field]?.value) {
               isNodeMissingRequiredField = true;
            }
         });
      } else {
         nodeComponentAction.forEach((action) => {
            if (!action.isRequired) return;

            if (!nodeFlowElementData || !nodeFlowElementData[action.field]?.value) {
               isNodeMissingRequiredField = true;
            }
         });
      }

      setHasMissingRequiredFields(isNodeMissingRequiredField);
   }, [data, currentSavedFlow, flows]);

   const connectorLogo = useMemo(() => {
      if (data && components) {
         const logo = components.find((connector) => connector.id === data.componentId)?.logo;
         const cacheBustedLogo = logo + "?t=" + Date.now();
         return cacheBustedLogo;
      }
   }, [data, components]);

   const flowReportStatus = useMemo(() => {
      const reports = flowReports[currentSavedFlow?.id]?.reports || [];
      const nodeReport = reports.findLast(({ payload }) => payload?.nodeId === id);

      return nodeReport ? nodeReport.payload.remarks : null;
   }, [currentSavedFlow?.id, flowReports, selectedLogNodeId]);

   useEffect(() => {
      if (toOpenNodeTab && flowElement && node?.id === toOpenNodeTab) {
         handleOpenComponentProps(FLEX_LAYOUT_COMPONENT.componentProps);
      }
   }, [toOpenNodeTab, node?.id]);

   const highlightNodeClass = () => {
      if (selectedLogNodeId === data.id || selected) {
         return "selected-node";
      }

      if (hasMissingRequiredField) {
         return "validation-failed-node";
      }

      if (executionPanelActive) {
         if (flowReportStatus === "Failed") return "failed-node";
         if (flowReportStatus === "Success") return "success-node";
      }

      return "";
   };

   return (
      <SoftBox
         draggable={!isLabel}
         onMouseEnter={() => {
            setShowTooltip(true);
         }}
         onMouseLeave={() => {
            setShowTooltip(false);
         }}
         onClick={(e) => {
            e.stopPropagation();

            if (executionPanelActive) {
               setNodes((prev) => prev.map((n) => ({ ...n, selected: n.id === id })));
               dispatch(setSelectedLogNodeId(id));
               return;
            }

            handleOpenComponentProps(FLEX_LAYOUT_COMPONENT.componentProps);
         }}
         onDragStart={(event) => {
            event.stopPropagation();
            drawerRef.current?.onNodeDragStart(event);
         }}
         onDragEnd={(event) => {
            event.stopPropagation();
            drawerRef.current?.onNodeDragEnd(event);
         }}
         onDragOver={(event) => {
            event.stopPropagation();
            drawerRef.current?.onNodeDragOver(event);
         }}
         onDragLeave={(event) => {
            event.stopPropagation();
            drawerRef.current?.onNodeDragLeave(event);
         }}
         onDrop={(event) => {
            event.stopPropagation();
            drawerRef.current?.onNodeDrop(event);
         }}
      >
         <ConditionalWrapper
            condition={!data.isDisabled && (flowElement?.data?.description?.value || data?.flowElement?.data?.description?.value || data?.description?.value)}
            wrapper={(children) => (
               <Tooltip
                  open={showTooltip}
                  placement={isVerticalCanvasDirection ? (isLabel ? "left" : "left") : "left"}
                  title={generateTooltip({
                     ...flowElement,
                     data: {
                        ...flowElement?.data,
                        ...data?.flowElement?.data,
                        ...data
                     }
                  })}
               >
                  {children}
               </Tooltip>
            )}
         >
            <SoftBox
               className={`custom-node-wrapper custom-node-wrapper-cover ${isVerticalCanvasDirection && "isVertical"} ${!isCommon && !isLabel ? "connector-node" : ""} ${isCommon ? "common-node" : ""} ${
                  data.isDisabled ? "disabled-node" : ""
               } ${highlightNodeClass()} ${isLabel ? "label-node" : ""} ${node.id}`}
            >
               <CustomNodeContext.Provider value={{ flowElement, updateFlowElement }}>
                  <ToggleDrawer ref={drawerRef} whatNode={node} isCommon={isCommon} isLabel={isLabel} logo={connectorLogo || connectorpng} />
               </CustomNodeContext.Provider>
               {data.notes?.content && <ButtonNotes type={data.notes?.type} handleOpenNoteDialog={handleOpenNoteDialog} />}
               {!isLabel && isFlowDraft && (
                  <ActionsWrapper isBooleanNode={isBooleanNode()} isCommon={isCommon}>
                     <ConditionalWrapper
                        condition={!data.isDisabled}
                        wrapper={(children) => (
                           <Tooltip title={t("buttons.execute_step")} placement="top">
                              {children}
                           </Tooltip>
                        )}
                     >
                        <IconButton
                           aria-label="execution"
                           size="small"
                           style={{
                              width: "100%",
                              height: "100%",
                              padding: 0,
                              opacity: data.isDisabled ? 0.3 : 1
                           }}
                           disabled={data.isDisabled}
                           onClick={executeNode}
                        >
                           <ThemedPlayArrowIcon width="20px" height="20px" color="currentColor" />
                        </IconButton>
                     </ConditionalWrapper>
                     <ButtonSwitchState title={t(`buttons.${data.isDisabled ? "enable_step" : "disable_step"}`)} toggleNodeState={toggleNodeState} allowEditWorkflow={allowEditWorkflow(isReadOnlyAccess)} />
                     <ButtonRename renameNode={renameNode} allowEditWorkflow={allowEditWorkflow(isReadOnlyAccess)} />
                     <ButtonCopyNode copyNode={copyNode} />
                     <ButtonCutNode cutNode={cutNode} />
                     <ButtonCloneNode cloneNode={handleCloneNode} allowEditWorkflow={allowEditWorkflow(isReadOnlyAccess)} />
                     <ButtonNotes isFromMenu handleOpenNoteDialog={handleOpenNoteDialog} />
                      <DeleteNodeMenuItem setShowTooltip={setShowTooltip} deleteNode={deleteNode} node={node} allowEditWorkflow={allowEditWorkflow(isReadOnlyAccess)} />
                  </ActionsWrapper>
               )}
               {data?.configFiles && renderConnectorVersionNumber()}
               <NodeLabel whatNode={node} flowElement={flowElement} isCommon={isCommon} isLabel={isLabel} />
               {data?.type === COMMON_COMPONENT_TYPES.SELECTION && !isFlowActive && allowEditWorkflow(isReadOnlyAccess) && (
                   <ButtonAddBranch onClick={handleAddBranchButtonClick} />
               )}
            </SoftBox>
         </ConditionalWrapper>
         <CustomNodeHandle isTarget />
         <CustomNodeHandle />

      </SoftBox>
   );
}
export default memo(CustomNode);
